import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Box,
  Button,
  Paper,
  Alert,
  Grid,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Chip,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  Switch,
  Tabs,
  Tab,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
} from '@mui/material';
import {
  CloudUpload,
  CloudDownload,
  PlayArrow,
  GetApp,
  Info,
  CheckCircle,
  Error as ErrorIcon,
  Description,
  Translate,
  Download,
  Clear,
  Language,
  TextFields,
  Article,
  MergeType,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useProcess } from '../context/ProcessContext';
import { useActivityLog } from '../context/ActivityLogContext';
import InterfaceHeader from '../components/InterfaceHeader';
import { API_ENDPOINTS, apiCall } from '../config/api';
import { TOOL_COLORS, getCardBackground } from '../constants/colors';

const DocumentTranslation = () => {
  const theme = useTheme();
  const { processes, addProcess, updateProcess, removeProcess } = useProcess();
  const {
    addActivityProcess,
    updateActivityProcess,
    setLogs: setActivityLogs,
    completeActivityProcess,
    setIntervalRef,
    clearIntervalRef,
  } = useActivityLog();
  const fileInputRef = useRef(null);
  const multiFileInputRef = useRef(null);
  const intervalRef = useRef(null);
  const processIdRef = useRef(null);
  const downloadTriggeredRef = useRef(false);

  // State management
  const [activeTab, setActiveTab] = useState(0);
  const [file, setFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [mergeDocuments, setMergeDocuments] = useState(false);
  const [euAssemblyMode, setEuAssemblyMode] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processId, setProcessId] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Language options
  const languages = [
    { code: 'es', name: 'Spanish', flag: '🇪🇸' },
    { code: 'fr', name: 'French', flag: '🇫🇷' },
    { code: 'de', name: 'German', flag: '🇩🇪' },
    { code: 'it', name: 'Italian', flag: '🇮🇹' },
    { code: 'nl', name: 'Dutch', flag: '🇳🇱' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
    { code: 'zh-CN', name: 'Chinese', flag: '🇨🇳' },
    { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
    { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
    { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
    { code: 'ko', name: 'Korean', flag: '🇰🇷' },
    { code: 'ru', name: 'Russian', flag: '🇷🇺' },
  ];

  // Poll for status when processing
  useEffect(() => {
    if (processId && (processing || (status && status.Status !== 'completed' && status.Status !== 'error'))) {
      intervalRef.current = setInterval(() => {
        fetchStatus();
      }, 1000);

      // Store interval in context for cleanup
      setIntervalRef(processId, intervalRef.current);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [processing, processId, status?.Status]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Handle single file selection
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  // Handle multiple file selection
  const handleMultiFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      validateAndSetMultiFiles(selectedFiles);
    }
  };

  // Validate single file
  const validateAndSetFile = (selectedFile) => {
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
    
    if (!validExtensions.includes(fileExtension)) {
      setError('Please select a valid Excel file (.xlsx, .xls) or CSV file for text translation');
      return;
    }

    if (selectedFile.size > 50 * 1024 * 1024) { // 50MB limit
      setError('File size must be less than 50MB');
      return;
    }

    setFile(selectedFile);
    setError(null);
    setSuccess(`File selected: ${selectedFile.name}`);
  };

  // Validate multiple files
  const validateAndSetMultiFiles = (selectedFiles) => {
    const validExtensions = ['.docx', '.doc', '.xlsx', '.xls', '.pdf', '.jpg', '.jpeg', '.png', '.tif'];
    const invalidFiles = [];

    for (const file of selectedFiles) {
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (!validExtensions.includes(fileExtension)) {
        invalidFiles.push(file.name);
      }
      if (file.size > 50 * 1024 * 1024) { // 50MB limit
        invalidFiles.push(`${file.name} (too large)`);
      }
    }

    if (invalidFiles.length > 0) {
      setError(`Invalid files: ${invalidFiles.join(', ')}`);
      return;
    }

    setFiles(selectedFiles);
    setError(null);
    setSuccess(`${selectedFiles.length} files selected`);
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      if (activeTab === 0) {
        validateAndSetFile(droppedFiles[0]);
      } else {
        validateAndSetMultiFiles(droppedFiles);
      }
    }
  };

  // Handle language selection
  const handleLanguageChange = (languageCode) => {
    setSelectedLanguages(prev => {
      if (prev.includes(languageCode)) {
        return prev.filter(lang => lang !== languageCode);
      } else {
        return [...prev, languageCode];
      }
    });
  };

  // Start text translation
  const startTextTranslation = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    if (selectedLanguages.length === 0) {
      setError('Please select at least one target language');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    selectedLanguages.forEach(lang => formData.append('targetLanguages', lang));

    try {
      setProcessing(true);
      setError(null);

      const data = await apiCall(API_ENDPOINTS.DOCUMENT_TRANSLATION_TRANSLATE_TEXT, {
        method: 'POST',
        body: formData,
      });

      if (data.success) {
        setProcessId(data.processId);
        processIdRef.current = data.processId;
        downloadTriggeredRef.current = false;

        // Add to global activity log context
        addActivityProcess(data.processId, 'Text Translation', 'Document Translation');

        // Add to process context
        addProcess({
          id: data.processId,
          name: 'Text Translation',
          progress: 0,
          status: 'processing'
        });
      } else {
        setError(data.message || 'Failed to start text translation');
        setProcessing(false);
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
      setProcessing(false);
    }
  };

  // Start document translation
  const startDocumentTranslation = async () => {
    if (files.length === 0) {
      setError('Please select files first');
      return;
    }

    if (selectedLanguages.length === 0) {
      setError('Please select at least one target language');
      return;
    }

    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    selectedLanguages.forEach(lang => formData.append('targetLanguages', lang));
    formData.append('mergeDocuments', mergeDocuments);

    try {
      setProcessing(true);
      setError(null);

      const data = await apiCall(API_ENDPOINTS.DOCUMENT_TRANSLATION_TRANSLATE_DOCUMENT, {
        method: 'POST',
        body: formData,
      });

      if (data.success) {
        setProcessId(data.processId);
        processIdRef.current = data.processId;
        downloadTriggeredRef.current = false;

        // Add to global activity log context
        addActivityProcess(data.processId, 'Document Translation', 'Document Translation');

        // Add to process context
        addProcess({
          id: data.processId,
          name: 'Document Translation',
          progress: 0,
          status: 'processing'
        });
      } else {
        setError(data.message || 'Failed to start document translation');
        setProcessing(false);
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
      setProcessing(false);
    }
  };

  // EU Assembly Instruction — translates to FR, DE, IT, NL, ES and merges all
  const startEUAssemblyTranslation = async () => {
    const euLanguages = ['fr', 'de', 'it', 'nl', 'es'];
    if (files.length === 0) {
      setError('Please upload your English assembly instruction file first');
      return;
    }

    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    euLanguages.forEach(lang => formData.append('targetLanguages', lang));
    formData.append('mergeDocuments', 'true');

    try {
      setProcessing(true);
      setError(null);
      setSelectedLanguages(euLanguages);
      setMergeDocuments(true);

      const data = await apiCall(API_ENDPOINTS.DOCUMENT_TRANSLATION_TRANSLATE_DOCUMENT, {
        method: 'POST',
        body: formData,
      });

      if (data.success) {
        setProcessId(data.processId);
        processIdRef.current = data.processId;
        downloadTriggeredRef.current = false;

        addActivityProcess(data.processId, 'EU Assembly Instruction Translation', 'Document Translation');
        addProcess({
          name: 'EU Assembly Instruction',
          status: 'processing',
          processId: data.processId,
          type: 'document-translation',
        });
      } else {
        setError(data.message || 'Failed to start EU translation');
        setProcessing(false);
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
      setProcessing(false);
    }
  };

  // Download result - defined first so it can be called by fetchStatus
  const downloadResult = async (downloadProcessId) => {
    if (!downloadProcessId) {
      console.error('No process ID available for download');
      setError('No process ID available for download');
      return;
    }

    console.log('=== DOWNLOAD STARTING ===');
    console.log('Download processId:', downloadProcessId);

    try {
      setError(null);

      const token = localStorage.getItem('jwtToken');
      const downloadUrl = `${API_ENDPOINTS.DOCUMENT_TRANSLATION_DOWNLOAD(downloadProcessId)}?t=${Date.now()}`;
      console.log('Download URL:', downloadUrl);

      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
      });

      console.log('Download response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Download failed:', response.status, errorText);
        throw new Error(`Download failed: ${response.status}`);
      }

      const blob = await response.blob();
      console.log('Blob received, size:', blob.size);

      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }

      // Get filename from response header if available
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `Translation_Results_${new Date().toISOString().slice(0, 19).replace(/[:-]/g, '').replace('T', '_')}.xlsx`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      // Clean up
      setTimeout(() => {
        if (document.body.contains(a)) {
          document.body.removeChild(a);
        }
        URL.revokeObjectURL(url);
      }, 1000);

      setSuccess('Translation results downloaded successfully!');
      console.log('=== DOWNLOAD COMPLETE ===');

    } catch (err) {
      console.error('Download error:', err);
      setError(`Download failed: ${err.message}`);
    }
  };

  // Fetch processing status
  const fetchStatus = async () => {
    const currentProcessId = processIdRef.current;
    if (!currentProcessId) {
      console.log('fetchStatus: No processId available');
      return;
    }

    try {
      const data = await apiCall(API_ENDPOINTS.DOCUMENT_TRANSLATION_STATUS(currentProcessId), {
        method: 'GET',
      });

      if (data.success) {
        const statusData = data.status;
        console.log('Status update:', statusData.status, 'Progress:', statusData.progressPercentage);
        setStatus(statusData);

        // Update global activity log
        updateActivityProcess(currentProcessId, {
          progressPercentage: statusData.progressPercentage || 0,
          totalItems: statusData.totalItems || 0,
          processedItems: statusData.processedItems || 0,
        });

        // Update logs in global context
        if (statusData.logs && statusData.logs.length > 0) {
          setActivityLogs(currentProcessId, statusData.logs);
        }

        // Update process context (camelCase properties)
        updateProcess(currentProcessId, {
          progress: statusData.progressPercentage || 0,
          status: statusData.status
        });

        // Check if processing is complete (camelCase: status)
        if (statusData.status === 'completed' || statusData.status === 'error') {
          console.log('=== PROCESS FINISHED ===', statusData.status);

          // Clear interval first
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            console.log('Interval cleared');
          }
          clearIntervalRef(currentProcessId);

          setProcessing(false);

          // Complete the activity process
          completeActivityProcess(currentProcessId, statusData.status === 'completed');

          if (statusData.status === 'completed' && !downloadTriggeredRef.current) {
            console.log('Triggering download, downloadTriggered:', downloadTriggeredRef.current);
            downloadTriggeredRef.current = true;
            setSuccess('Translation completed successfully! Downloading results...');

            // Call download with the processId
            console.log('Calling downloadResult with:', currentProcessId);
            downloadResult(currentProcessId);
          } else if (statusData.status === 'error') {
            setError('Translation failed. Check the logs for details.');
          }
        }
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  };

  // Download template
  const downloadTemplate = async () => {
    try {
      const response = await apiCall(API_ENDPOINTS.DOCUMENT_TRANSLATION_TEMPLATE, {
        method: 'GET',
      });

      const blob = await response.blob();

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `Translation_Template_${new Date().toISOString().slice(0, 19).replace(/[:-]/g, '').replace('T', '')}.xlsx`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setSuccess('Template downloaded successfully!');
    } catch (err) {
      setError(`Download failed: ${err.message}`);
    }
  };

  // Clear file selection
  const clearFiles = () => {
    if (activeTab === 0) {
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } else {
      setFiles([]);
      if (multiFileInputRef.current) {
        multiFileInputRef.current.value = '';
      }
    }
    setSuccess(null);
  };

  // Reset form
  const handleReset = () => {
    setFile(null);
    setFiles([]);
    setSelectedLanguages([]);
    setMergeDocuments(false);
    setProcessing(false);
    setProcessId(null);
    processIdRef.current = null;
    downloadTriggeredRef.current = false;
    setStatus(null);
    setError(null);
    setSuccess(null);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (multiFileInputRef.current) {
      multiFileInputRef.current.value = '';
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto', position: 'relative' }}>
      <InterfaceHeader
        title="Document Translation"
        color={TOOL_COLORS.OTHER.primary}
        category="other"
      />

      <Grid container spacing={3}>
        {/* Left Column - Configuration */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Translation Setup
            </Typography>

            {/* Translation Type Tabs */}
            <Tabs
              value={activeTab}
              onChange={(e, value) => setActiveTab(value)}
              variant="fullWidth"
              sx={{ mb: 3, '& .MuiTab-root.Mui-selected': { color: TOOL_COLORS.OTHER.primary }, '& .MuiTabs-indicator': { bgcolor: TOOL_COLORS.OTHER.primary } }}
            >
              <Tab 
                icon={<TextFields />} 
                label="Text Translation" 
                sx={{ textTransform: 'none' }}
              />
              <Tab 
                icon={<Article />} 
                label="Document Translation" 
                sx={{ textTransform: 'none' }}
              />
            </Tabs>

            {/* Language Selection */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Target Languages
              </Typography>
              <FormGroup>
                <Grid container spacing={1}>
                  {languages.map((lang) => (
                    <Grid item xs={6} sm={4} key={lang.code}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedLanguages.includes(lang.code)}
                            onChange={() => handleLanguageChange(lang.code)}
                            size="small"
                            sx={{ '&.Mui-checked': { color: TOOL_COLORS.OTHER.primary } }}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <span>{lang.flag}</span>
                            <span style={{ fontSize: '0.875rem' }}>{lang.name}</span>
                          </Box>
                        }
                        sx={{ margin: 0 }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>
              {selectedLanguages.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Selected: {selectedLanguages.length} languages
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Tab Content */}
            {activeTab === 0 ? (
              // Text Translation
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Upload Excel/CSV File
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  Excel (.xlsx, .xls) or CSV files with text to translate
                </Typography>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                
                <Box
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  sx={{
                    border: `2px dashed ${isDragging ? TOOL_COLORS.OTHER.primary : theme.palette.divider}`,
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    backgroundColor: isDragging ? 
                      theme.palette.action.hover : 
                      'transparent',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: TOOL_COLORS.OTHER.primary,
                      backgroundColor: theme.palette.action.hover,
                    }
                  }}
                  onClick={() => !file && fileInputRef.current?.click()}
                >
                  {file ? (
                    <Box>
                      <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                      <Typography variant="body1" gutterBottom>
                        {file.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Button
                          size="small"
                          startIcon={<Clear />}
                          onClick={(e) => {
                            e.stopPropagation();
                            clearFiles();
                          }}
                        >
                          Remove File
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <>
                      <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body1" gutterBottom>
                        Drop your file here or click to browse
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Excel (.xlsx, .xls) or CSV files (max 50MB)
                      </Typography>
                    </>
                  )}
                </Box>

                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={downloadTemplate}
                  sx={{ mt: 2, py: 1.2, textTransform: 'none', fontWeight: 600,
                    borderColor: TOOL_COLORS.OTHER.primary, color: TOOL_COLORS.OTHER.primary,
                    '&:hover': { borderColor: TOOL_COLORS.OTHER.hover, bgcolor: TOOL_COLORS.OTHER.light } }}
                  fullWidth
                >
                  DOWNLOAD TEMPLATE
                </Button>
              </Box>
            ) : (
              // Document Translation
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Upload Documents
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  Word, Excel, PDF, or Image files • Multiple files supported
                </Typography>

                {/* Merge Documents Option */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={mergeDocuments || euAssemblyMode}
                      onChange={(e) => { if (!euAssemblyMode) setMergeDocuments(e.target.checked); }}
                      disabled={euAssemblyMode}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: TOOL_COLORS.OTHER.primary }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: TOOL_COLORS.OTHER.primary } }}
                    />
                  }
                  label="Merge translated documents"
                  sx={{ mb: 0 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={euAssemblyMode}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setEuAssemblyMode(on);
                        if (on) {
                          setSelectedLanguages(['fr', 'de', 'it', 'nl', 'es']);
                          setMergeDocuments(true);
                        }
                      }}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#003399' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#003399' } }}
                    />
                  }
                  label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><span>🇪🇺</span><span>EU Assembly Instruction</span><Chip label="FR · DE · IT · NL · ES" size="small" sx={{ height: 20, fontSize: 9, ml: 0.5, bgcolor: euAssemblyMode ? '#00339915' : 'transparent', color: euAssemblyMode ? '#003399' : 'text.secondary', border: `1px solid ${euAssemblyMode ? '#00339940' : 'transparent'}` }} /></Box>}
                  sx={{ mb: 2 }}
                />
                
                <input
                  ref={multiFileInputRef}
                  type="file"
                  accept=".docx,.doc,.xlsx,.xls,.pdf,.jpg,.jpeg,.png,.tif"
                  multiple
                  onChange={handleMultiFileSelect}
                  style={{ display: 'none' }}
                />
                
                <Box
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  sx={{
                    border: `2px dashed ${isDragging ? TOOL_COLORS.OTHER.primary : theme.palette.divider}`,
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    backgroundColor: isDragging ? 
                      theme.palette.action.hover : 
                      'transparent',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: TOOL_COLORS.OTHER.primary,
                      backgroundColor: theme.palette.action.hover,
                    }
                  }}
                  onClick={() => files.length === 0 && multiFileInputRef.current?.click()}
                >
                  {files.length > 0 ? (
                    <Box>
                      <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                      <Typography variant="body1" gutterBottom>
                        {files.length} file{files.length > 1 ? 's' : ''} selected
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {files.map(f => f.name).join(', ')}
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Button
                          size="small"
                          startIcon={<Clear />}
                          onClick={(e) => {
                            e.stopPropagation();
                            clearFiles();
                          }}
                        >
                          Clear Files
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <>
                      <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body1" gutterBottom>
                        Drop your documents here or click to browse
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Multiple files supported: Word, Excel, PDF, Images (max 50MB each)
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            )}

            {/* Action Buttons */}
            <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={processing ? <CircularProgress size={20} color="inherit" /> : <Translate />}
                onClick={activeTab === 0 ? startTextTranslation : (euAssemblyMode ? startEUAssemblyTranslation : startDocumentTranslation)}
                disabled={processing || (activeTab === 0 ? !file : files.length === 0) || (!euAssemblyMode && selectedLanguages.length === 0)}
                fullWidth
                size="large"
                sx={{ bgcolor: TOOL_COLORS.OTHER.primary, '&:hover': { bgcolor: TOOL_COLORS.OTHER.hover } }}
              >
                {processing ? 'Translating...' : 'Start Translation'}
              </Button>
              

              {(file || files.length > 0 || processing || status) && (
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  disabled={processing}
                >
                  Reset
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Right Column - Information */}
        <Grid item xs={12} md={6}>
          {/* Status Messages */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {/* Download Result */}
          {status?.Status === 'completed' && (
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'success.light', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ color: 'success.dark', mb: 2 }}>
                Translation Complete!
              </Typography>
              <Button
                variant="contained"
                startIcon={<CloudDownload />}
                onClick={downloadResult}
                fullWidth
                color="success"
              >
                Download Results
              </Button>
            </Paper>
          )}

          {/* Translation Capabilities */}
          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Translation Capabilities
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              This tool uses Azure AI Translation services to provide accurate translations in multiple languages.
              For text translation, upload Excel or CSV files with text content. For document translation,
              upload Word documents, PDFs, Excel files, or images.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label="13 Languages" size="small" />
              <Chip label="Azure AI Powered" size="small" />
              <Chip label="Batch Processing" size="small" />
              <Chip label="Document Merge" size="small" sx={{ bgcolor: `${TOOL_COLORS.OTHER.primary}20`, color: TOOL_COLORS.OTHER.primary }} />
            </Box>
          </Paper>

          {/* Translation Tips */}
          <Paper sx={{ p: 3, bgcolor: `${TOOL_COLORS.OTHER.primary}10`, border: `1px solid ${TOOL_COLORS.OTHER.primary}30` }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: TOOL_COLORS.OTHER.primary }}>
              Translation Tips
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                <li>Text mode: Excel/CSV with text in first column</li>
                <li>Document mode: Preserves formatting and layout</li>
                <li>Images are converted to PDF for translation</li>
                <li>Use merge option to combine all translations</li>
                <li>Activity logs appear in floating panel when translation starts</li>
              </Box>
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DocumentTranslation;